import React, { useState, useEffect } from "react";
import axios from "axios";
import { Lock, Users, Calendar, MousePointerClick, TrendingUp, ChevronLeft, ChevronRight, ChevronDown, Search, AlertTriangle, Mail, Clock, CheckCircle, Eye, X, Send, Rocket } from "lucide-react";
import { getMaintenanceStatus } from "../lib/maintenance";
import "../styles/admin.css";

const ADMIN_PASSWORD = "East!West!Uk123";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  
  // Data states
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    today: 0,
    funnel: 0,
    exitPopup: 0
  });
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  
  // Email visibility states
  const [emailTab, setEmailTab] = useState("sent"); // "sent" or "scheduled"
  const [sentEmails, setSentEmails] = useState([]);
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [emailStats, setEmailStats] = useState({ total_sent: 0, total_scheduled: 0, total_leads: 0 });
  const [emailLoading, setEmailLoading] = useState(true);
  const [emailSectionOpen, setEmailSectionOpen] = useState(false); // Collapsed by default
  const [appLaunched, setAppLaunched] = useState(false);
  
  // Email preview modal state
  const [previewModal, setPreviewModal] = useState({ open: false, html: "", email: "", type: "" });
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Send email states
  const [sendingEmail, setSendingEmail] = useState(null); // email being sent
  const [sendingAll, setSendingAll] = useState(null); // template being sent to all
  
  // Launch modal state
  const [launchModal, setLaunchModal] = useState({ open: false, step: 1 }); // step 1: preview, step 2: confirm
  const [launchPromoCode, setLaunchPromoCode] = useState("LAUNCH25");
  const [launching, setLaunching] = useState(false);
  const [launchResult, setLaunchResult] = useState(null);
  const [launchEmailPreview, setLaunchEmailPreview] = useState("");
  const [launchPreviewLoading, setLaunchPreviewLoading] = useState(false);
  
  // Maintenance state
  const [maintenance, setMaintenance] = useState({ enabled: false, message: '' });
  
  // Filter states
  const [sourceFilter, setSourceFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [emailSearchQuery, setEmailSearchQuery] = useState("");
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [emailPage, setEmailPage] = useState(1);
  const leadsPerPage = 20;
  const emailsPerPage = 15;

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setError("");
      sessionStorage.setItem("adminAuth", "true");
    } else {
      setError("Access denied");
    }
  };

  useEffect(() => {
    // Check if already authenticated this session
    if (sessionStorage.getItem("adminAuth") === "true") {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
      fetchEmailData();
      checkMaintenance();
    }
  }, [isAuthenticated]);

  const checkMaintenance = async () => {
    const status = await getMaintenanceStatus(true); // Force refresh
    setMaintenance(status);
  };

  const fetchData = async () => {
    setLoading(true);
    setFetchError("");
    try {
      // Fetch leads from backend API (uses Supabase service role key)
      const response = await axios.get(`${BACKEND_URL}/api/admin/leads`);
      const { leads: fetchedLeads, stats: fetchedStats } = response.data;

      setLeads(fetchedLeads || []);
      setStats({
        total: fetchedStats?.total || 0,
        today: fetchedStats?.today || 0,
        funnel: fetchedStats?.funnel || 0,
        exitPopup: fetchedStats?.exit_popup || 0
      });
    } catch (err) {
      console.error('Error fetching leads:', err);
      setFetchError(err.response?.data?.detail || 'Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailData = async () => {
    setEmailLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/emails`);
      const { sent_emails, scheduled_emails, stats: emailStatsData, app_launched } = response.data;
      
      setSentEmails(sent_emails || []);
      setScheduledEmails(scheduled_emails || []);
      setEmailStats(emailStatsData || { total_sent: 0, total_scheduled: 0, total_leads: 0 });
      setAppLaunched(app_launched || false);
    } catch (err) {
      console.error('Error fetching email data:', err);
    } finally {
      setEmailLoading(false);
    }
  };

  // Send a single email immediately
  const sendEmailNow = async (email, template, emailType) => {
    if (!window.confirm(`Send "${emailType}" to ${email} now?`)) return;
    
    setSendingEmail(`${email}-${template}`);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/send-email`, { email, template });
      alert(`✅ ${emailType} sent to ${email}!`);
      fetchEmailData(); // Refresh
    } catch (err) {
      alert(`❌ Failed to send: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSendingEmail(null);
    }
  };

  // Send email type to all users
  const sendAllEmails = async (template, emailType) => {
    const count = scheduledEmails.filter(e => e.template === template).length;
    if (!window.confirm(`Send "${emailType}" to ALL ${count} users now?\n\nThis cannot be undone.`)) return;
    
    setSendingAll(template);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/send-all`, { template });
      alert(`✅ ${response.data.message}`);
      fetchEmailData(); // Refresh
    } catch (err) {
      alert(`❌ Failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSendingAll(null);
    }
  };

  // Open launch modal and fetch preview
  const openLaunchModal = async () => {
    setLaunchModal({ open: true, step: 1 });
    setLaunchResult(null);
    setLaunchPreviewLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/email-preview/launch_day?email=preview@example.com`);
      setLaunchEmailPreview(response.data.html);
    } catch (err) {
      console.error('Error fetching launch email preview:', err);
      setLaunchEmailPreview("<p style='color: red; padding: 20px;'>Failed to load preview</p>");
    } finally {
      setLaunchPreviewLoading(false);
    }
  };

  // Launch the app
  const handleLaunch = async () => {
    setLaunching(true);
    setLaunchResult(null);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/launch`, {
        promo_code: launchPromoCode,
        confirm: true
      });
      setLaunchResult({ success: true, ...response.data });
      setAppLaunched(true);
      fetchEmailData(); // Refresh to show updated status
    } catch (err) {
      setLaunchResult({ success: false, message: err.response?.data?.detail || err.message });
    } finally {
      setLaunching(false);
    }
  };

  const openEmailPreview = async (template, email, emailType) => {
    setPreviewLoading(true);
    setPreviewModal({ open: true, html: "", email, type: emailType });
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/email-preview/${template}?email=${encodeURIComponent(email)}`);
      setPreviewModal({ open: true, html: response.data.html, email, type: emailType });
    } catch (err) {
      console.error('Error fetching email preview:', err);
      setPreviewModal({ open: true, html: "<p style='color: red; padding: 20px;'>Failed to load email preview</p>", email, type: emailType });
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewModal({ open: false, html: "", email: "", type: "" });
  };

  // Filter leads
  const filteredLeads = leads.filter(lead => {
    const matchesSource = sourceFilter === "all" || lead.source === sourceFilter;
    const matchesSearch = !searchQuery || 
      lead.email.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSource && matchesSearch;
  });

  // Filter emails
  const currentEmailList = emailTab === "sent" ? sentEmails : scheduledEmails;
  const filteredEmails = currentEmailList.filter(item => {
    return !emailSearchQuery || 
      item.email.toLowerCase().includes(emailSearchQuery.toLowerCase());
  });

  // Paginate leads
  const totalPages = Math.ceil(filteredLeads.length / leadsPerPage);
  const startIndex = (currentPage - 1) * leadsPerPage;
  const paginatedLeads = filteredLeads.slice(startIndex, startIndex + leadsPerPage);

  // Paginate emails
  const totalEmailPages = Math.ceil(filteredEmails.length / emailsPerPage);
  const emailStartIndex = (emailPage - 1) * emailsPerPage;
  const paginatedEmails = filteredEmails.slice(emailStartIndex, emailStartIndex + emailsPerPage);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="admin-login-container">
        <div className="admin-login-card">
          <div className="admin-login-icon">
            <Lock size={32} />
          </div>
          <h1>Admin Access</h1>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
            />
            <button type="submit">Access Dashboard</button>
          </form>
          {error && <p className="admin-error">{error}</p>}
        </div>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="admin-dashboard">
      {/* Email Preview Modal */}
      {previewModal.open && (
        <div className="email-preview-modal-overlay" onClick={closePreview}>
          <div className="email-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                <Mail size={20} />
                <span>{previewModal.type}</span>
              </div>
              <div className="modal-subtitle">To: {previewModal.email}</div>
              <button className="modal-close" onClick={closePreview}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {previewLoading ? (
                <div className="modal-loading">Loading preview...</div>
              ) : (
                <iframe
                  srcDoc={previewModal.html}
                  title="Email Preview"
                  className="email-preview-iframe"
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Launch Modal - Multi-step */}
      {launchModal.open && (
        <div className="email-preview-modal-overlay" onClick={() => !launching && setLaunchModal({ open: false, step: 1 })}>
          <div className={`launch-modal ${launchModal.step === 1 ? 'with-preview' : ''}`} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header launch-modal-header">
              <div className="modal-title">
                <Rocket size={24} />
                <span>Launch Thryvin'</span>
              </div>
              <button className="modal-close" onClick={() => !launching && setLaunchModal({ open: false, step: 1 })}>
                <X size={20} />
              </button>
            </div>
            <div className="launch-modal-body">
              {launchResult ? (
                <div className={`launch-result ${launchResult.success ? 'success' : 'error'}`}>
                  {launchResult.success ? (
                    <>
                      <div className="launch-success-icon">🚀</div>
                      <h3>Thryvin' is LIVE!</h3>
                      <p>Sent launch email to <strong>{launchResult.sent_count}</strong> users</p>
                      <p>Promo code: <strong>{launchResult.promo_code}</strong></p>
                      <button className="launch-done-btn" onClick={() => setLaunchModal({ open: false, step: 1 })}>
                        Done
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="launch-error-icon">❌</div>
                      <h3>Launch Failed</h3>
                      <p>{launchResult.message}</p>
                      <button className="launch-retry-btn" onClick={() => setLaunchResult(null)}>
                        Try Again
                      </button>
                    </>
                  )}
                </div>
              ) : launchModal.step === 1 ? (
                /* Step 1: Preview the email */
                <>
                  <p className="launch-step-label">Step 1 of 2: Preview Launch Email</p>
                  
                  <div className="launch-promo-input">
                    <label>Promo Code (shown in email)</label>
                    <input
                      type="text"
                      value={launchPromoCode}
                      onChange={(e) => setLaunchPromoCode(e.target.value.toUpperCase())}
                      placeholder="e.g. LAUNCH25"
                    />
                  </div>
                  
                  <div className="launch-preview-container">
                    <p className="launch-preview-label">This email will be sent to all {stats.total} users:</p>
                    {launchPreviewLoading ? (
                      <div className="launch-preview-loading">Loading preview...</div>
                    ) : (
                      <iframe
                        srcDoc={launchEmailPreview}
                        title="Launch Email Preview"
                        className="launch-preview-iframe"
                      />
                    )}
                  </div>
                  
                  <div className="launch-actions">
                    <button 
                      className="launch-cancel-btn"
                      onClick={() => setLaunchModal({ open: false, step: 1 })}
                    >
                      Cancel
                    </button>
                    <button 
                      className="launch-next-btn"
                      onClick={() => setLaunchModal({ open: true, step: 2 })}
                      disabled={!launchPromoCode}
                    >
                      Next: Confirm Launch
                    </button>
                  </div>
                </>
              ) : (
                /* Step 2: Are you sure? Confirmation */
                <>
                  <p className="launch-step-label">Step 2 of 2: Confirm Launch</p>
                  
                  <div className="launch-confirm-box">
                    <div className="launch-confirm-icon">⚠️</div>
                    <h3>Are you sure?</h3>
                    <p>This action <strong>cannot be undone</strong>.</p>
                    
                    <ul className="launch-confirm-list">
                      <li>✉️ Send launch email to <strong>{stats.total} users</strong></li>
                      <li>🎟️ Include promo code: <strong>{launchPromoCode}</strong></li>
                      <li>🚫 Cancel all scheduled emails (Week 1, Week 3)</li>
                    </ul>
                  </div>
                  
                  <div className="launch-actions">
                    <button 
                      className="launch-back-btn"
                      onClick={() => setLaunchModal({ open: true, step: 1 })}
                      disabled={launching}
                    >
                      ← Back
                    </button>
                    <button 
                      className="launch-confirm-btn"
                      onClick={handleLaunch}
                      disabled={launching}
                    >
                      <Rocket size={18} />
                      {launching ? "Launching..." : "Yes, Launch Now!"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Maintenance Banner */}
      {maintenance.enabled && (
        <div className="admin-maintenance-banner">
          <AlertTriangle size={18} />
          <span><strong>Maintenance mode is ON</strong> — {maintenance.message}</span>
        </div>
      )}

      <header className="admin-header">
        <h1>Thryvin' Leads Dashboard</h1>
        <button 
          className="admin-logout"
          onClick={() => {
            sessionStorage.removeItem("adminAuth");
            setIsAuthenticated(false);
          }}
        >
          Logout
        </button>
      </header>

      {loading ? (
        <div className="admin-loading">Loading...</div>
      ) : (
        <>
          {/* Stats Cards - NOW AT TOP */}
          <div className="admin-stats-grid">
            <div className="admin-stat-card">
              <div className="stat-icon total">
                <Users size={24} />
              </div>
              <div className="stat-content">
                <span className="stat-value">{stats.total}</span>
                <span className="stat-label">Total Leads</span>
              </div>
            </div>

            <div className="admin-stat-card">
              <div className="stat-icon today">
                <Calendar size={24} />
              </div>
              <div className="stat-content">
                <span className="stat-value">{stats.today}</span>
                <span className="stat-label">Today</span>
              </div>
            </div>

            <div className="admin-stat-card">
              <div className="stat-icon funnel">
                <TrendingUp size={24} />
              </div>
              <div className="stat-content">
                <span className="stat-value">{stats.funnel}</span>
                <span className="stat-label">Funnel</span>
              </div>
            </div>

            <div className="admin-stat-card">
              <div className="stat-icon exit">
                <MousePointerClick size={24} />
              </div>
              <div className="stat-content">
                <span className="stat-value">{stats.exitPopup}</span>
                <span className="stat-label">Exit Popup</span>
              </div>
            </div>
          </div>

          {/* Launch Button - Only show if not launched yet */}
          {!appLaunched && (
            <div className="launch-section">
              <button 
                className="launch-btn"
                onClick={openLaunchModal}
              >
                <Rocket size={20} />
                Launch App & Send to All
              </button>
              <span className="launch-hint">
                Sends launch email to all {stats.total} users and cancels scheduled emails
              </span>
            </div>
          )}

          {appLaunched && (
            <div className="launched-banner">
              <Rocket size={18} />
              <span>🎉 App Launched! All users have been notified.</span>
            </div>
          )}

          {/* Email Visibility Section - COLLAPSIBLE */}
          <div className={`admin-section email-section ${emailSectionOpen ? 'open' : 'collapsed'}`}>
            <div 
              className="section-header clickable"
              onClick={() => setEmailSectionOpen(!emailSectionOpen)}
            >
              <div className="section-header-left">
                <ChevronDown size={20} className={`chevron ${emailSectionOpen ? 'open' : ''}`} />
                <h2><Mail size={20} /> Email Status</h2>
              </div>
              <div className="section-header-info">
                <span className="email-summary">
                  {emailStats.total_sent} sent • {emailStats.total_scheduled} scheduled
                </span>
                <button 
                  className="refresh-btn-small" 
                  onClick={(e) => { e.stopPropagation(); fetchEmailData(); }}
                >
                  Refresh
                </button>
              </div>
            </div>
            
            {emailSectionOpen && (
              <div className="email-section-content">
                {/* Email Tabs */}
                <div className="email-tabs">
                  <button 
                    className={`email-tab ${emailTab === "sent" ? "active" : ""}`}
                    onClick={() => { setEmailTab("sent"); setEmailPage(1); setEmailSearchQuery(""); }}
                  >
                    <CheckCircle size={16} />
                    Sent Emails
                    <span className="tab-count">{emailStats.total_sent}</span>
                  </button>
                  <button 
                    className={`email-tab ${emailTab === "scheduled" ? "active" : ""}`}
                    onClick={() => { setEmailTab("scheduled"); setEmailPage(1); setEmailSearchQuery(""); }}
                  >
                    <Clock size={16} />
                    Scheduled Emails
                    <span className="tab-count">{emailStats.total_scheduled}</span>
                  </button>
                </div>

                {emailLoading ? (
                  <div className="admin-loading">Loading emails...</div>
                ) : (
                  <>
                    {/* Email Search + Send All buttons for scheduled */}
                    <div className="email-filters">
                      <div className="filter-group search">
                        <Search size={18} />
                        <input
                          type="text"
                          placeholder="Search by email..."
                          value={emailSearchQuery}
                          onChange={(e) => {
                            setEmailSearchQuery(e.target.value);
                            setEmailPage(1);
                          }}
                        />
                      </div>
                      
                      {/* Send All buttons - only show for scheduled tab */}
                      {emailTab === "scheduled" && scheduledEmails.length > 0 && !appLaunched && (
                        <div className="send-all-buttons">
                          {scheduledEmails.some(e => e.template === "whats_coming") && (
                            <button 
                              className="send-all-btn"
                              onClick={() => sendAllEmails("whats_coming", "Week 1 Sneak Peek")}
                              disabled={sendingAll === "whats_coming"}
                            >
                              <Send size={14} />
                              {sendingAll === "whats_coming" ? "Sending..." : "Send All Week 1"}
                            </button>
                          )}
                          {scheduledEmails.some(e => e.template === "launch_bonus") && (
                            <button 
                              className="send-all-btn"
                              onClick={() => sendAllEmails("launch_bonus", "Week 3 Perks")}
                              disabled={sendingAll === "launch_bonus"}
                            >
                              <Send size={14} />
                              {sendingAll === "launch_bonus" ? "Sending..." : "Send All Week 3"}
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Email Table */}
                    <div className="admin-table-container">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>Email Address</th>
                            <th>Email Type</th>
                            <th>{emailTab === "sent" ? "Sent At" : "Scheduled For"}</th>
                            <th>Status</th>
                            <th>{emailTab === "scheduled" ? "Actions" : "Preview"}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedEmails.length === 0 ? (
                            <tr>
                              <td colSpan="5" className="no-data">
                                {emailTab === "sent" 
                                  ? "No emails have been sent yet" 
                                  : "No emails currently scheduled"}
                              </td>
                            </tr>
                          ) : (
                            paginatedEmails.map((item, index) => (
                              <tr key={`${item.email}-${item.email_type}-${index}`}>
                                <td className="email-cell">{item.email}</td>
                                <td className="email-type-cell">{item.email_type}</td>
                                <td className="date-cell">
                                  {formatDate(emailTab === "sent" ? item.sent_at : item.scheduled_for)}
                                </td>
                                <td>
                                  <span className={`status-badge ${item.status.toLowerCase()}`}>
                                    {item.status}
                                  </span>
                                </td>
                                <td className="actions-cell">
                                  <button 
                                    className="preview-btn"
                                    onClick={() => openEmailPreview(item.template, item.email, item.email_type)}
                                    title="Preview email"
                                  >
                                    <Eye size={16} />
                                  </button>
                                  {emailTab === "scheduled" && (
                                    <button 
                                      className="send-now-btn"
                                      onClick={() => sendEmailNow(item.email, item.template, item.email_type)}
                                      disabled={sendingEmail === `${item.email}-${item.template}`}
                                      title="Send now"
                                    >
                                      <Send size={14} />
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Email Pagination */}
                    {totalEmailPages > 1 && (
                      <div className="admin-pagination">
                        <button
                          onClick={() => setEmailPage(p => Math.max(1, p - 1))}
                          disabled={emailPage === 1}
                        >
                          <ChevronLeft size={18} />
                        </button>
                        <span>Page {emailPage} of {totalEmailPages}</span>
                        <button
                          onClick={() => setEmailPage(p => Math.min(totalEmailPages, p + 1))}
                          disabled={emailPage === totalEmailPages}
                        >
                          <ChevronRight size={18} />
                        </button>
                      </div>
                    )}

                    <div className="admin-footer">
                      Showing {paginatedEmails.length} of {filteredEmails.length} {emailTab === "sent" ? "sent" : "scheduled"} emails
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Filters */}
          <div className="admin-filters">
            <div className="filter-group">
              <label>Source:</label>
              <select 
                value={sourceFilter} 
                onChange={(e) => {
                  setSourceFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All Sources</option>
                <option value="funnel">Funnel</option>
                <option value="exit_popup">Exit Popup</option>
              </select>
            </div>

            <div className="filter-group search">
              <Search size={18} />
              <input
                type="text"
                placeholder="Search email..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            <button className="refresh-btn" onClick={fetchData}>
              Refresh
            </button>
          </div>

          {/* Leads Table */}
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Source</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {paginatedLeads.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="no-data">No leads found</td>
                  </tr>
                ) : (
                  paginatedLeads.map((lead) => (
                    <tr key={lead.id}>
                      <td className="email-cell">{lead.email}</td>
                      <td>
                        <span className={`source-badge ${lead.source}`}>
                          {lead.source === 'exit_popup' ? 'Exit Popup' : 'Funnel'}
                        </span>
                      </td>
                      <td className="date-cell">{formatDate(lead.created_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="admin-pagination">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={18} />
              </button>
              <span>Page {currentPage} of {totalPages}</span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight size={18} />
              </button>
            </div>
          )}

          <div className="admin-footer">
            Showing {paginatedLeads.length} of {filteredLeads.length} leads
          </div>
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
